<template>
    <div :class="['om-balloon relative', {'office-page': officePage}]">
        <div
            v-if="balloonsTotal > 1"
            class="om-balloon-switcher"
        >
            <span class="om-balloon-switcher__text">
                {{ balloonIndex + 1 }}&nbsp;/&nbsp;{{ balloonsTotal }}&nbsp;{{ typeMap[type] }}
            </span>
            <div class="flex items-center gap-8 mt-4">
                <UIButton
                    flat
                    :disabled="!balloonIndex"
                    class="om-balloon-switcher__button om-balloon-switcher__button--rotated"
                    @click="$emit('switch', -1)"
                >
                    <ArrowSvg />
                </UIButton>
                <UIButton
                    flat
                    :disabled="balloonIndex === balloonsTotal - 1"
                    class="om-balloon-switcher__button"
                    @click="$emit('switch', 1)"
                >
                    <ArrowSvg />
                </UIButton>
            </div>
        </div>
        <UIButton
            v-if="!officePage"
            flat
            class="om-balloon__close"
            @click="$emit('close')"
        >
            <template #icon>
                <CloseSvg />
            </template>
        </UIButton>
        <div
            :class="[
                'om-balloon-inner custom-scroll',
                {'office-page': officePage, 'om-balloon-inner--higer': balloonsTotal > 1 && !officePage}
            ]"
        >
            <div class="om-balloon-wrapper">
                <div class="om-balloon-header-wrapper flex items-start justify-between">
                    <div class="om-balloon-header">
                        <div class="flex flex-column justify-between">
                            <h1
                                v-if="balloonData.title && officePage"
                                class="h1 om-balloon__title--office-page mb-54"
                            >
                                {{ balloonData.title }}
                            </h1>
                            <h3
                                v-if="balloonData.title && !officePage"
                                class="om-balloon__title mb-24 pr-16"
                            >
                                {{ balloonData.title }}
                            </h3>
                            <nuxt-img
                                v-if="imageSrc"
                                class="om-balloon__title-img mb-24"
                                :src="imageSrc"
                                :alt="balloonData.title"
                                format="webp"
                                loading="lazy"
                            />
                        </div>
                        <div
                            :class="[
                                'om-balloon-location flex items-center mb-24',
                            ]"
                        >
                            <LocationSvg class="om-balloon-location__icon mr-8" />
                            <p class="om-balloon-location__address pr-16 text">
                                <span
                                    v-if="balloonData.address"
                                    class="inline-block"
                                >
                                    {{ balloonData.city.title }}, {{ balloonData.address }}
                                </span>
                                <span v-else>{{ getFormattedAddress(balloonData.address_full) }}</span>
                            </p>
                        </div>
                        <UIButton
                            v-if="isRemoteOfficeAvailable(balloonData)"
                            size="xs"
                            label="Записаться на посещение офиса"
                            class="mb-32"
                            @click="onConfirmRemoteOfficeRecording(balloonData)"
                        />
                    </div>
                    <div
                        v-if="imageSrc"
                        :class="['om-balloon__img', {pointer: officePage}]"
                        @click="onScaleImageHandler(imageSrc)"
                    >
                        <nuxt-img
                            :src="imageSrc"
                            :alt="balloonData.title"
                            format="webp"
                            loading="lazy"
                        />
                    </div>
                </div>
                <template v-if="balloonData.temporaryNotices && balloonData.temporaryNotices.length">
                    <p
                        v-for="(notice, index) in balloonData.temporaryNotices"
                        :key="`notice__${index}`"
                        class="text text-semibold text-green mb-32 pl-24"
                    >
                        {{ notice }}
                    </p>
                </template>
                <div :class="['om-balloon-working', `om-balloon-working--${type}`]">
                    <OfficesBalloonExpandedItem
                        v-for="({title, content, iconName, titleClasses}, index) in expandedItemData"
                        :key="title + index"
                        :title="title"
                        :content="content"
                        :icon-name="iconName"
                        :title-classes="titleClasses"
                    />
                </div>
                <div
                    v-if="balloonData.phoneNumbers && balloonData.phoneNumbers.length"
                    class="om-balloon-phones flex"
                >
                    <PhoneSvg class="om-balloon-phones__icon mr-8" />
                    <div class="om-balloon-phones-wrapper flex">
                        <h4 class="text fz-16 mb-16 text-bold">Контакты</h4>
                        <div class="om-balloon-phones__phones">
                            <template v-for="(phoneNumber, index) in balloonData.phoneNumbers">
                                <div
                                    v-if="isAllowed(phoneNumber)"
                                    :key="`phone__${index}`"
                                >
                                    <span class="block mb-4 text text-semibold">{{ phoneNumber.title }}</span>
                                    <a
                                        class="block mb-16 text"
                                        :href="`tel:${phoneNumber.number.replace(/[^\d]/g, '')}`"
                                    >
                                        {{ phoneNumber.number }}
                                    </a>
                                </div>
                            </template>
                            <div v-if="balloonData.email_delivery_business_loan_docs">
                                <span class="block mb-4 text text-semibold">
                                    Email отделения
                                </span>
                                <a
                                    :href="`mailto:${balloonData.email_delivery_business_loan_docs}`"
                                    class="block mb-16 text"
                                >
                                    {{ balloonData.email_delivery_business_loan_docs }}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
                <n-link
                    v-if="!officePage && type === 'office'"
                    :to="getPath(balloonData.id)"
                    class="om-balloon__link step-link mt-24"
                >
                    <span>Подробнее</span>
                    <ChevronSvg />
                </n-link>
                <div class="clearfix" />
            </div>
        </div>
        <transition
            name="fade"
            mode="out-in"
        >
            <div
                v-if="scaledImageSrc"
                class="scaled-image pointer"
                @click="scaledImageSrc = null"
            >
                <nuxt-img
                    class="scaled-image__image"
                    :src="scaledImageSrc"
                    alt=""
                    format="webp"
                    loading="lazy"
                />
            </div>
        </transition>
    </div>
</template>

<script>
import {computed, defineComponent, ref} from 'vue';
import {UIButton} from '@cibsite/ui-components-vue2';

import {useOfficeRemoteEntry} from '@/core/composables/useOfficeRemoteEntry';
import {useFilesPath} from '@/core/composables/useFilesPath';

import {servicesMap} from '@/core/models/pages/aboutPage/OfficesModel';
import LocationSvg from '@/assets/images/icons/location.svg?inline';
import PhoneSvg from '@/assets/images/icons/phone.svg?inline';
import ChevronSvg from '@/assets/images/icons/arrows/chevron-down-bold.svg?inline';
import CloseSvg from '@/assets/images/icons/close.svg?inline';
import ArrowSvg from '@/assets/images/icons/arrows/arrow-right.svg?inline';
import OfficesBalloonExpandedItem from '@/components/Pages/About/Offices/OfficesBalloonExpandedItem.vue';

export default defineComponent({
    name: 'OfficesBalloonContent',
    components: {
        LocationSvg,
        PhoneSvg,
        ChevronSvg,
        CloseSvg,
        ArrowSvg,
        UIButton,
        OfficesBalloonExpandedItem,
    },
    props: {
        balloonData: {
            type: Object,
            required: true,
        },
        type: {
            type: String,
            default: () => '',
        },
        officePage: {
            type: Boolean,
            default: () => false,
        },
        balloonsTotal: {
            type: Number,
            default: () => 1,
        },
        balloonIndex: {
            type: Number,
            default: () => 0,
        },
    },
    setup(props) {
        const {
            isRemoteOfficeAvailable,
            onConfirmRemoteOfficeRecording,
        } = useOfficeRemoteEntry();
        const {officeImagePath} = useFilesPath();

        const scaledImageSrc = ref(null);

        const imageSrc = computed(() => {
            return props.balloonData.photo
                ? `/site${officeImagePath(props.balloonData.photo)}`
                : '';
        });
        const balloonServices = computed(() => {
            return Object.keys(props.balloonData)
                .filter((key) => props.balloonData[key])
                .reduce((acc, service) => {
                    if (servicesMap[props.type][service]) {
                        acc.push(servicesMap[props.type][service]);
                    }

                    return acc;
                }, []);
        });
        const expandedItemData = computed(() => {
            return [
                {
                    title: props.type === 'office' ? 'Физ. лица' : 'Время работы',
                    content: props.balloonData.work_time,
                },
                {
                    title: 'юр. лица',
                    content: props.balloonData.work_time_legal_entities,
                },
                {
                    title: 'VIP-зал',
                    content: props.balloonData.work_time_vip,
                },
                {
                    title: 'Сбор биометрических данных и подтверждение учетной записи на госуслугах*',
                    content: props.balloonData.work_time_bio_data_collection,
                    iconName: 'ExpandSvg',
                    titleClasses: 'increased-line-height',
                },
                {
                    title: 'Услуги',
                    content: balloonServices.value.map((service, index) => {
                        return `
                            <span class="block">
                                ${service}${(index + 1) === balloonServices.value.length ? '' : ','}
                            </span>
                        `;
                    }).join(''),
                    iconName: 'ServicesSvg',
                },
            ];
        });

        function isAllowed(phoneNumber) {
            if (phoneNumber.hideOnSite) {
                return false;
            }

            if (new Date().getDay() === 6) {
                return !phoneNumber.hideOnSaturday;
            }

            return true;
        }

        function getFormattedAddress(address) {
            return address?.split(',').slice(1).join(',') ?? '';
        }

        function getPath(id) {
            return `/about/offices-and-atms/${props.type}?id=${id}`;
        }

        function onScaleImageHandler(src) {
            if (props.officePage) {
                scaledImageSrc.value = src;
            }
        }

        return {
            typeMap: {
                atm: 'банкоматов',
                terminal: 'терминалов',
                office: 'офисов',
            },
            scaledImageSrc,
            imageSrc,
            expandedItemData,
            isRemoteOfficeAvailable,
            onConfirmRemoteOfficeRecording,
            isAllowed,
            getFormattedAddress,
            getPath,
            onScaleImageHandler,
        };
    },
});
</script>

<style lang="scss">
@import '@/assets/styles/components/about/offices/offices-balloon';
</style>
